/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:35:58AM]
Permissions to update outside the main branch: [true]
*/

import { DesignType, DocumentTitleProvider, NotFound, useCondition } from '@finbeat/react-toolkit';
import { library } from '@fortawesome/fontawesome-svg-core';
import * as Icons from '@fortawesome/free-solid-svg-icons';
import { IconDefinition, IconPack, IconPrefix } from '@fortawesome/free-solid-svg-icons';
import ForgotPassword from 'components/ForgotPassword';
import Home from 'components/Home';
import Layout from 'components/Layout/Layout';
import Login from 'components/Login';
import LoginRedirect from 'components/LoginRedirect';
import Logout from 'components/Logout';
import MainSpace from 'components/MainSpace/MainSpace';
import ResetPassword from 'components/ResetPassword';
import Brands from 'components/Workplaces/Brands/Brands';
import Clients from 'components/Workplaces/Clients/Clients';
import DeclinedShowcase from 'components/Workplaces/DeclinedShowcase/DeclinedShowcases';
import Dictionary from 'components/Workplaces/Dictionary/Dictionary';
import FundsTransferDetails from 'components/Workplaces/FundsTransferDetails/FundsTransferDetails';
import FundsTransfers from 'components/Workplaces/FundsTransfers/FundsTransfers';
import { HistTable } from 'components/Workplaces/HistTable/HistTable';
import Leadgens from 'components/Workplaces/Leadgen/Leadgen';
import Order from 'components/Workplaces/Order/Order';
import OrderChangePage from 'components/Workplaces/Order/OrderChangePage';
import OrderStatus from 'components/Workplaces/OrderStatus/OrderStatus';
import Outlets from 'components/Workplaces/Outlets/Outlets';
import Payments from 'components/Workplaces/Payments/Payments';
import PaymentsToClients from 'components/Workplaces/PaymentsToClients/PaymentsToClients';
import Prescr from 'components/Workplaces/Prescr/Prescr';
import Questionary from 'components/Workplaces/Questionary/Questionary';
import RejectedTraffic from 'components/Workplaces/RejectedTraffic/RejectedTraffic';
import ReportDetails from 'components/Workplaces/ReportDetails/ReportDetails';
import Reports from 'components/Workplaces/Reports/Reports';
import Roles from 'components/Workplaces/Roles/Roles';
import ScoringSettings from 'components/Workplaces/ScoringSettings/ScoringSettings';
import ServiceDiscount from 'components/Workplaces/ServiceDiscount/ServiceDiscount';
import Settings from 'components/Workplaces/Settings/Settings';
import SmsLogs from 'components/Workplaces/SmsLogs/SmsLogs';
import TariffGroups from 'components/Workplaces/TariffGroups/TariffGroups';
import TariffProcedureParams from 'components/Workplaces/TariffProcedureParams/TariffProcedureParams';
import Tariffs from 'components/Workplaces/Tariffs/Tariffs';
import Translate from 'components/Workplaces/Translate/Translate';
import UserActionLog from 'components/Workplaces/UserActionLog/UserActionLog';
import Users from 'components/Workplaces/Users/Users';
import TechnicalWorks from 'components/Workplaces/WorkplaceSettings/TechnicalWorks';
import WorkplaceSettings from 'components/Workplaces/WorkplaceSettings/WorkplaceSettings';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { RouteObject, useRoutes } from 'react-router-dom';
import Merchant from './components/Workplaces/Merchant/Merchant';
import Preorder from './components/Workplaces/Preorders/Preorder';
import TradeOrgs from './components/Workplaces/TradeOrgs/TradeOrgs';

// Type that `library.add()` expects.
type IconDefinitionOrPack = IconDefinition | IconPack;

interface ImportedIcons {
    [key: string]: IconPrefix | IconDefinitionOrPack;
}

const iconList = Object.keys(Icons)
    .filter((key) => key !== 'fas' && key !== 'prefix')
    .map((icon) => (Icons as ImportedIcons)[icon]);

library.add(...(iconList as IconDefinitionOrPack[]));

export default function App() {
    const { instanceName, designType } = useGeneralSettingsContext();

    const workplaces = [
        { path: '*', element: <NotFound /> },
        { path: '/', element: <Home /> },
        { path: '/translation', element: <Translate /> },
        { path: '/dictionary', element: <Dictionary /> },
        { path: '/useractionlog', element: <UserActionLog /> },
        { path: '/settings', element: <Settings /> },
        { path: '/orderstatus', element: <OrderStatus /> },
        { path: '/tariffgroups', element: <TariffGroups /> },
        { path: '/tariffs', element: <Tariffs /> },
        { path: '/brands', element: <Brands /> },
        { path: '/fundstransfers', element: <FundsTransfers /> },
        { path: '/questionary', element: <Questionary /> },
        { path: '/scoringsettings', element: <ScoringSettings /> },
        { path: '/smslog', element: <SmsLogs /> },
        { path: '/order', element: <Order /> },
        { path: '/order/:id', element: <OrderChangePage /> },
        { path: '/payments', element: <Payments /> },
        { path: '/histtables', element: <HistTable /> },
        { path: '/clients', element: <Clients /> },
        { path: '/clients/:id', element: <Clients /> },
        { path: '/users', element: <Users /> },
        { path: '/roles', element: <Roles /> },
        {
            path: '/fundstransferdetails',
            element: <FundsTransferDetails />,
        },
        { path: '/declinedshowcase', element: <DeclinedShowcase /> },
        {
            path: '/tariffprocedureparams',
            element: <TariffProcedureParams />,
        },
        { path: '/reports', element: <Reports /> },
        { path: '/reportdetails/:id', element: <ReportDetails /> },
        { path: '/leadgen', element: <Leadgens /> },
        { path: '/paymentstoclients', element: <PaymentsToClients /> },
        { path: '/prescr', element: <Prescr /> },
        { path: '/rejectedtraffic', element: <RejectedTraffic /> },
        { path: '/servicediscount', element: <ServiceDiscount /> },
        { path: '/tradeorgs', element: <TradeOrgs /> },
        { path: '/outlets', element: <Outlets /> },
        { path: '/merchant', element: <Merchant /> },
        { path: '/preorder', element: <Preorder /> },
    ];

    const routes = useCondition<DesignType, (content: RouteObject) => RouteObject>(
        designType,
        {
            nova: (content: RouteObject) => ({
                path: '/',
                element: <MainSpace />,
                children: [content],
            }),
        },
        (content: RouteObject) => content
    );

    const element = useRoutes([
        {
            path: '/',
            element: <Layout />,
            children: [
                routes({
                    path: '/',
                    element: <LoginRedirect />,
                    children: [
                        {
                            path: '/',
                            element: <TechnicalWorks />,
                            children: workplaces,
                        },
                        {
                            path: '/workplacesettings',
                            element: <WorkplaceSettings />,
                        },
                    ],
                }),
                { path: '/login', element: <Login /> },
                { path: '/logout', element: <Logout /> },
                { path: '/reset-password', element: <ResetPassword /> },
                { path: '/forgot-password', element: <ForgotPassword /> },
            ],
        },
    ]);

    return (
        <DocumentTitleProvider instanceName={instanceName ?? ''}>{element}</DocumentTitleProvider>
    );
}
